import { Reference } from 'rc-table';
import { SimplePagination } from '@ui/SimplePagination';
import { TableRef } from 'antd/es/table';
import { UpdateQueryParams } from '@hooks';

import { KeyPaginationQueryParams, PaginatedResource, PaginationDirection } from '@sonar-software/sonar-ts-types';
import React, { useCallback } from 'react';

function getScrollableElement(element: HTMLElement | null): HTMLElement | null {
	if (!element) {
		return null;
	}

	if (element && element.scrollTop) {
		return element;
	}

	for (let i = 0; i < element.children.length; i++) {
		const child = element.children[ i ];

		const result = getScrollableElement(child as HTMLElement);

		if (result) {
			return result;
		}
	}

	return null;
}

export const keyPaginationDefaultQueryParams: KeyPaginationQueryParams = {
	count: 20,
	key: '',
	paginationDirection: PaginationDirection.Forward
};

export const keyPaginationFallbackQueryParams: Partial<KeyPaginationQueryParams> = {
	key: '',
	paginationDirection: PaginationDirection.Forward
};

export interface KeyPaginationProps {
	autoHide?: boolean;
	className?: string;
	defaultPageSize?: number;
	listRef?: React.RefObject<Reference> | React.RefObject<HTMLDivElement> | React.RefObject<TableRef>;
	resource: PaginatedResource<unknown, unknown> | null | undefined;
	onChange: UpdateQueryParams<KeyPaginationQueryParams>;
	showSizeChanger?: boolean;
}

export const KeyPagination: React.FC<KeyPaginationProps> = ({
	listRef,
	resource,
	onChange,
	showSizeChanger,
	...rest
}) => {
	const handlePaginationChange = useCallback(
		(count: number, direction?: PaginationDirection) => {
			if (direction) {
				onChange({
					count,
					key: direction === PaginationDirection.Forward ? resource?.nextKey : resource?.prevKey,
					paginationDirection: direction
				});
			} else {
				onChange({
					count,
					key: undefined,
					paginationDirection: PaginationDirection.Forward
				});
			}

			setTimeout(() => {
				if (listRef?.current) {
					const element = (listRef.current as Reference).nativeElement ??
							(listRef.current as HTMLDivElement);

					const target = getScrollableElement(element);

					if (target) {
						target.scrollTop = 0;
					}
				}
			}, 250);
		},
		[ listRef, resource?.nextKey, resource?.prevKey, onChange ]
	);

	const results = resource?.results ?? [];

	if (!results.length) {
		return null;
	}

	return (
		<SimplePagination
			{ ...rest }
			enableNext={ Boolean(resource?.nextKey) }
			enablePrev={ Boolean(resource?.prevKey) }
			onChange={ handlePaginationChange }
			showSizeChanger={ showSizeChanger } />
	);
};
